import { getFetcher, putFetcher } from '@/api/fetchers';
import { type Service } from '@/schemas/API/GET/service/response';
import {
  Button,
  Paper,
  Skeleton,
  Stack,
  Textarea,
  TextInput,
} from '@mantine/core';
import { useParams } from 'react-router-dom';
import useSWR, { mutate } from 'swr';
import { ButtonBar } from '@/components/Form/ButtonBar/ButtonBar';
import { useEffect, useState } from 'react';
import {
  goodNotification,
  okNotification,
} from '@/components/Notification/Notification';
import { useForm } from 'react-hook-form';
import { displayErrorOnUseState } from '@/utils/error/errorHandler';

export const DetailsPage = (): JSX.Element => {
  const { serviceId } = useParams<{ serviceId: string }>();
  const { data: service } = useSWR<Service>(
    `/services/${serviceId}`,
    getFetcher
  );

  interface FormValues {
    name: string;
    code: string;
    desc: string | null;
    price: number
  }

  const { register, handleSubmit, reset } = useForm<FormValues>();

  const [nameError, setNameError] = useState('');
  const [codeError, setCodeError] = useState('');
  const [priceError, setPriceError] = useState('');

  const updateServiceDetails = async () => {
    mutate(`/services/${serviceId}`);
  };

  // Initialize form fields when fetcher receives data from BE
  useEffect(() => {
    if (service) {
      reset({
        name: service.name,
        code: service.code,
        desc: service.desc,
        price: service.price, // Initialize price
      });
    }
  }, [service]);

  const updateDetails = handleSubmit(async (data: FormValues) => {
    setNameError('');
    setCodeError('');
    setPriceError('');

    try {
      if (data.desc === '') {
        await putFetcher(`/services/${serviceId}`, {
          name: data.name,
          code: data.code,
          price: data.price,
        });
      } else {
        await putFetcher(`/services/${serviceId}`, data);
      }
      goodNotification('Úspěch!', 'Služba je upravená');
    } catch (err) {
      displayErrorOnUseState(err, [
        { field: 'name', setState: setNameError },
        { field: 'code', setState: setCodeError },
        { field: 'price', setState: setPriceError },
      ]);
    } finally {
      updateServiceDetails();
    }
  });

  function handleCancel(): void {
    setNameError('');
    setCodeError('');
    setPriceError('');
    reset(service);

    okNotification('Storno...', 'Služba je teď v původním stavu');
  }

  return (
    <Stack className="">
      {service == null ? (
        <Skeleton className="flex-grow" />
      ) : (
        <Paper withBorder radius="md" className="flex p-4 gap-8">
          <Stack className="gap-8">
            <TextInput
              {...register('name')}
              label="Název služby"
              name="name"
              variant="filled"
              size="md"
              radius="md"
              error={nameError}
              withErrorStyles={false}
            />
            <TextInput
              {...register('code')}
              label="Kód služby"
              name="code"
              variant="filled"
              size="md"
              radius="md"
              error={codeError}
              withErrorStyles={false}
            />
            <TextInput
                {...register('price', { valueAsNumber: true })}
                variant="filled"
                label="Cena služby"
                name="price"
                size="md"
                radius="md"
                error={priceError}
                withErrorStyles={false}
            />
          </Stack>
          <Textarea
            {...register('desc')}
            label="Popis služby"
            name="desc"
            variant="filled"
            autosize
            minRows={5}
            maxRows={8}
            size="md"
            radius="md"
            className="flex-grow"
            withErrorStyles={false}
          />
        </Paper>
      )}
      <ButtonBar>
        <Button
          variant="outline"
          radius="md"
          size="md"
          className="text-lg"
          onClick={handleCancel}
        >
          Storno
        </Button>
        <Button
          radius="md"
          size="md"
          className="text-lg"
          onClick={updateDetails}
        >
          Uložit
        </Button>
      </ButtonBar>
    </Stack>
  );
};

export {};
